/**
 * Category Loader Module
 * @description 카테고리 데이터 로드, 매핑, 캐싱 처리
 *
 * 사용법:
 * 1. 고도몰 템플릿에서 설정:
 *    window.__NDE_SIDE_MENU__ = 'godo';  // 또는 'static'
 *    window.__NDE_SIDE_CUSTOM_MENUS__ = [{ title: '링크', link: '/path', isOpenNewTab: false }];
 *
 * 2. 스크립트에서 호출:
 *    const categories = await loadCategories();
 */

import { getCategoryCache } from './cache';
import type { Category, CustomMenuItem } from './types';

/**
 * 고도몰 API 카테고리 응답 타입
 */
interface GodoCategoryResponse {
  cateCd?: string;
  cateNm?: string;
  children?: GodoCategoryResponse[];
}

const GODO_API_URL = '../goods/goods_ps.php';

/**
 * 설정값 조회
 */
const getMenuSource = () => window.__NDE_SIDE_MENU__ ?? 'godo';
const getStaticCategories = () => window.__NDE_SIDE_MENU_CATEGORIES__ ?? [];
const getCustomMenus = () => window.__NDE_SIDE_CUSTOM_MENUS__?.filter((menu) => !menu.hidden) ?? [];

/**
 * 고도몰 카테고리 응답을 Category로 매핑
 */
const mapGodoCategory = (cat: GodoCategoryResponse): Category => {
  const item: Category = {
    id: cat.cateCd || '',
    label: cat.cateNm || '',
    href: `../goods/goods_list.php?cateCd=${cat.cateCd || ''}`,
    isOpenNewTab: false,
  };

  if (Array.isArray(cat.children) && cat.children.length > 0) {
    item.children = cat.children.map(mapGodoCategory);
  }

  return item;
};

/**
 * 커스텀 메뉴 아이템을 Category로 매핑
 */
const mapCustomItem = (item: CustomMenuItem): Category | null => {
  if (!item.title || !item.link) {
    return null;
  }

  return {
    id: `custom_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    label: item.title,
    href: item.link,
    isOpenNewTab: item.isOpenNewTab === true,
  };
};

/**
 * 커스텀 메뉴 아이템들을 카테고리에 추가
 */
const appendCustomItems = (categories: Category[]): Category[] => {
  const customMenus = getCustomMenus();
  if (customMenus.length === 0) {
    return categories;
  }

  const mappedItems = customMenus.map(mapCustomItem).filter((item): item is Category => item !== null);

  return mappedItems.length > 0 ? [...categories, ...mappedItems] : categories;
};

/**
 * 고도몰 API 응답 파싱
 */
const isGodoCategoryEntry = (entry: unknown): entry is GodoCategoryResponse => {
  return entry !== null && typeof entry === 'object' && 'cateCd' in entry;
};

const parseGodoResponseEntry = (
  entry: unknown,
  pushIfValid: (cat: GodoCategoryResponse) => void,
  processGroup: (group: unknown) => void
): void => {
  if (Array.isArray(entry)) {
    entry.forEach(pushIfValid);
  } else if (isGodoCategoryEntry(entry)) {
    pushIfValid(entry);
  } else if (entry && typeof entry === 'object') {
    Object.values(entry).forEach(processGroup);
  }
};

const parseGodoResponse = (res: unknown): Category[] => {
  const result: Category[] = [];

  const pushIfValid = (cat: GodoCategoryResponse) => {
    if (cat?.cateCd) {
      result.push(mapGodoCategory(cat));
    }
  };

  const processGroup = (group: unknown) => {
    if (Array.isArray(group)) {
      group.forEach(pushIfValid);
    }
  };

  if (Array.isArray(res)) {
    res.forEach((entry) => {
      parseGodoResponseEntry(entry, pushIfValid, processGroup);
    });
  } else if (res && typeof res === 'object') {
    Object.values(res).forEach(processGroup);
  }

  return result;
};

/**
 * 고도몰 API에서 카테고리 fetch
 */
const fetchFromGodo = async (): Promise<Category[]> => {
  const cache = getCategoryCache<Category>();

  const response = await fetch(GODO_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: 'mode=get_all_category',
  });

  const res = await response.json();

  if (!res || res === 'false') {
    return appendCustomItems([]);
  }

  const categories = parseGodoResponse(res);
  if (categories.length > 0) {
    cache.set(categories);
  }

  return appendCustomItems(categories);
};

/**
 * 카테고리 로드 (설정값 기반)
 *
 * - static: __NDE_SIDE_MENU_CATEGORIES__ + 커스텀 메뉴
 * - godo: 고도몰 API에서 로드 + 커스텀 메뉴 추가
 */
export const loadCategories = async (): Promise<Category[]> => {
  const source = getMenuSource();

  if (source === 'static') {
    // static 모드: 전역 변수에서 카테고리 참조 + 커스텀 메뉴
    return appendCustomItems(getStaticCategories());
  }

  // godo 모드: 캐시 확인 → API 호출
  const cache = getCategoryCache<Category>();
  const cachedData = cache.get();

  if (cachedData && cachedData.length > 0) {
    return appendCustomItems(cachedData);
  }

  return fetchFromGodo();
};

/**
 * 캐시 무효화 후 재로드
 */
export const refreshCategories = (): Promise<Category[]> => {
  const cache = getCategoryCache<Category>();
  cache.clear();
  return fetchFromGodo();
};

/**
 * 현재 메뉴 소스 타입 확인
 */
export const isGodoSource = () => getMenuSource() === 'godo';
export const isStaticSource = () => getMenuSource() === 'static';
